import type {
    CapabilityKey,
    CapabilityStatus,
    DailyCapabilityDto,
    DailyIncidentDto,
    DailyPlatformStatusDto,
    DailyScopeDto,
    DailyWindowDto,
    EvidenceMode,
    HealthState,
    IncidentLifecycle,
    IncidentSeverity,
    MetricDto,
    MetricQuality,
    MoneyByCurrencyDto,
    ReasonCode,
} from "@/features/daily/types/daily.types";

export type DailyTone = "neutral" | "info" | "warning" | "critical";

type WindowLabel = DailyWindowDto["label"];
type RuleId = DailyIncidentDto["ruleId"];

const ABSENT = "—";

const countFormatter = new Intl.NumberFormat("es-ES", { useGrouping: "always" });
// es-ES leaves four-digit amounts ungrouped by default; Daily groups them («1.234,50»).
const amountFormatter = new Intl.NumberFormat("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: "always" });
const oneDecimal = new Intl.NumberFormat("es-ES", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
// D130: KPI figures (tiles, charts, growth table) keep the legacy board's en-US format —
// «€561.0k», «6,805», «2.30%», «2.69 h» — while running text stays es-ES.
const kpiOne = new Intl.NumberFormat("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const kpiTwo = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const kpiWhole = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

const compactNumber = (value: number): string => {
    const abs = Math.abs(value);
    const inThousands = Math.round(abs / 100) / 10;
    if (abs < 1000) return kpiWhole.format(value);
    if (inThousands < 1000) return `${kpiOne.format(Math.sign(value) * inThousands)}k`;
    return `${kpiOne.format(Math.sign(value) * (Math.round(abs / 100_000) / 10))}M`;
};

const currencySymbol = (currency: string): string => {
    try {
        const part = new Intl.NumberFormat("en-US", { style: "currency", currency }).formatToParts(0).find((piece) => piece.type === "currency");
        return part === undefined ? currency : part.value;
    } catch {
        return currency;
    }
};
const clockFormatter = new Intl.DateTimeFormat("es-ES", { timeZone: "Europe/Madrid", hour: "2-digit", minute: "2-digit" });
const stampFormatter = new Intl.DateTimeFormat("es-ES", {
    timeZone: "Europe/Madrid",
    dateStyle: "short",
    timeStyle: "short",
});

// Fabricated-data honesty: an absent number is rendered as "—", never as 0 and
// never as NaN. Every formatter below takes the null case explicitly.
export const dailyFormat = {
    absent: ABSENT,

    count: (value: number | null): string => (value === null ? ABSENT : countFormatter.format(value)),

    ratioPercent: (value: number | null): string => (value === null ? ABSENT : `${oneDecimal.format(value * 100)}%`),

    /** No FX anywhere in Daily: an amount is only ever shown next to its own currency. */
    money: (amount: number, currency: string | null): string =>
        currency === null
            ? `${amountFormatter.format(amount)} (moneda no registrada)`
            : `${amountFormatter.format(amount)} ${currency}`,

    stamp: (at: number | null): string => (at === null ? ABSENT : stampFormatter.format(new Date(at))),

    /**
     * KPI value (D130): auto-compact with the symbol first — «€561.0k», «€3.5M»; a code
     * that has no symbol (a ticker, «VES») follows the number: «12.5k USDT».
     */
    compactMoney: (amount: number | null, currency: string | null): string => {
        if (amount === null) return ABSENT;
        if (currency === null) return compactNumber(amount);
        const symbol = currencySymbol(currency);
        const sign = amount < 0 ? "-" : "";
        return /^[A-Za-z]+$/.test(symbol) ? `${compactNumber(amount)} ${symbol}` : `${sign}${symbol}${compactNumber(Math.abs(amount))}`;
    },

    /** The exact KPI amount for a tooltip or screen readers: «€3,528,760.82». */
    exactMoney: (amount: number | null, currency: string | null): string => {
        if (amount === null) return ABSENT;
        if (currency === null) return `${kpiTwo.format(amount)} (moneda no registrada)`;
        try {
            return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount);
        } catch {
            return `${kpiTwo.format(amount)} ${currency}`;
        }
    },

    /** A KPI count: grouped below 100,000 («6,805»), compact above («250.0k»). */
    compactCount: (value: number | null): string => {
        if (value === null) return ABSENT;
        return Math.abs(value) < 100_000 ? kpiWhole.format(value) : compactNumber(value);
    },

    /** A KPI count in full: «6,805». */
    exactCount: (value: number | null): string => (value === null ? ABSENT : kpiWhole.format(value)),

    /** A KPI rate: two decimals for rates and take rate («2.30%»), one for shares («9.3%»). */
    kpiPercent: (ratio: number | null, decimals: 1 | 2): string =>
        ratio === null ? ABSENT : `${(decimals === 1 ? kpiOne : kpiTwo).format(ratio * 100)}%`,

    /** A KPI duration: «30 s», «7 min», «2.69 h». */
    kpiDuration: (ms: number | null): string => {
        if (ms === null) return ABSENT;
        if (ms < 60_000) return `${Math.round(ms / 1_000)} s`;
        if (ms < 3_600_000) return `${Math.round(ms / 60_000)} min`;
        return `${kpiTwo.format(ms / 3_600_000)} h`;
    },

    /** A KPI delta without its sign — the arrow beside it says the direction: «11.3%», «0.34 pp». */
    kpiDelta: (change: number, unit: "%" | " pp"): string =>
        `${(unit === "%" ? kpiOne : kpiTwo).format(Math.abs(change))}${unit}`,

    /** Madrid wall-clock time, for a board read at a glance. */
    clock: (at: number | null): string => (at === null ? ABSENT : clockFormatter.format(new Date(at))),

    /** A count with its noun agreeing in number: "1 operación", "3 operaciones". */
    counted: (value: number, singular: string, plural: string): string =>
        `${countFormatter.format(value)} ${value === 1 ? singular : plural}`,

    /** A duration in the unit a person reads it in: seconds, minutes, hours or days. */
    duration: (ms: number | null): string => {
        if (ms === null) return ABSENT;
        if (ms < 60_000) return `${Math.round(ms / 1_000)} s`;
        if (ms < 3_600_000) return `${Math.round(ms / 60_000)} min`;
        if (ms < 172_800_000) return `${oneDecimal.format(ms / 3_600_000)} h`;
        return `${oneDecimal.format(ms / 86_400_000)} d`;
    },

    durationHours: (ms: number | null): string => (ms === null ? ABSENT : `${oneDecimal.format(ms / 3_600_000)} h`),

    /** Enmienda C9: two different configurations must never look like one. */
    policy: (policyVersion: number, policyFingerprint: string): string =>
        `política v${policyVersion} · ${policyFingerprint.slice(0, 8)}`,
};

// L44: every map below is TOTAL over its closed vocabulary. tsc rejects the file
// if the backend adds a literal, so a new status can never fall through to a
// default that happens to look healthy.
const REASON_TEXT: Record<ReasonCode, string> = {
    insufficient_sample: "No hay operaciones suficientes en la ventana para dar un valor",
    no_evidence: "Esta señal no produjo evidencia",
    evidence_stale: "La evidencia más reciente es más antigua de lo que admite esta comprobación",
    capability_not_instrumented: "Esta capacidad todavía no está instrumentada",
    capability_unknown: "Esta capacidad no produjo evidencia dentro de su ventana de validez",
    stage_not_applicable: "Esta etapa no aplica a la ruta observada",
    stage_applicability_unknown: "No hay evidencia positiva de si esta etapa aplica",
    status_deleted: "La operación fue eliminada",
    payout_failed: "El pago al destino falló después de un estado correcto",
    credit_stalled: "El abono de un depósito está detenido más allá de su umbral",
    validation_blocked: "La operación está retenida por bloqueos de validación",
    cross_currency_unsupported: "No se suman importes en monedas distintas",
    zero_denominator: "La población de esta proporción está vacía",
    single_scope: "Solo hay un espacio de trabajo en el alcance: la concentración no se puede medir",
    evaluation_already_applied: "Esta evaluación ya se aplicó",
    policy_changed: "Cambió la política que hay detrás de esta señal",
    too_many_docs: "La población es demasiado grande para calcular esta métrica",
    source_degraded: "La fuente respondió solo en parte",
    index_missing: "El registro que alimenta esta sección no está disponible",
    expected_quiet: "Horario tranquilo: es normal que no haya operaciones ahora",
};

const HEALTH_TEXT: Record<HealthState, string> = {
    operational: "Operativa",
    degraded: "Degradada",
    outage: "Caída",
    unverifiable: "No verificable",
};

const HEALTH_TONE: Record<HealthState, DailyTone> = {
    operational: "neutral",
    degraded: "warning",
    outage: "critical",
    unverifiable: "info",
};

const QUALITY_TONE: Record<MetricQuality, DailyTone> = {
    ok: "neutral",
    partial: "warning",
    stale: "warning",
    // SC-023: too little evidence is not a failure. `insufficient` never earns red.
    insufficient: "info",
    unsupported: "info",
    error: "critical",
};

const QUALITY_TEXT: Record<MetricQuality, string> = {
    ok: "Medido",
    partial: "Evidencia parcial",
    stale: "Evidencia atrasada",
    insufficient: "Evidencia insuficiente",
    unsupported: "La fuente no lo admite",
    error: "La fuente falló",
};

const CAPABILITY_TEXT: Record<CapabilityKey, string> = {
    access: "Acceso",
    creation: "Crear operación",
    funds_received: "Recibir fondos",
    processing: "Procesamiento",
    delivery: "Entrega",
    data_freshness: "Frescura de datos",
};

const CAPABILITY_STATUS_TEXT: Record<CapabilityStatus, string> = {
    ok: "Verificada",
    expected_quiet: "Tranquilo (esperado)",
    degraded: "Degradada",
    unknown: "Sin evidencia vigente",
    not_instrumented: "Sin instrumentar",
};

const CAPABILITY_STATUS_TONE: Record<CapabilityStatus, DailyTone> = {
    ok: "neutral",
    // B6/D108: a quiet hour is expected — information, never a warning.
    expected_quiet: "info",
    degraded: "warning",
    unknown: "info",
    not_instrumented: "info",
};

// FR-007/SC-007: only positively verified evidence counts as healthy. Anything
// unknown or uninstrumented is visible and is NEVER green.
const CAPABILITY_COUNTS_AS_HEALTHY: Record<CapabilityStatus, boolean> = {
    ok: true,
    // Expected silence is not positive evidence: it is never painted green (FR-007).
    expected_quiet: false,
    degraded: false,
    unknown: false,
    not_instrumented: false,
};

const EVIDENCE_MODE_TEXT: Record<EvidenceMode, string> = {
    operation_events: "Eventos de operaciones",
    projection_freshness: "Frescura de la proyección",
    http_probe: "Sonda de disponibilidad",
    not_instrumented: "Sin instrumentación",
};

const WINDOW_TEXT: Record<WindowLabel, string> = {
    today: "Hoy",
    month_to_date: "Mes en curso",
    last_full_month: "Último mes completo",
    custom: "Rango personalizado",
    previous_period: "Periodo anterior",
    previous_month: "Mes anterior",
    historical_mean: "Media histórica",
};

const SEVERITY_TEXT: Record<IncidentSeverity, string> = {
    info: "Informativa",
    minor: "Menor",
    major: "Mayor",
    critical: "Crítica",
};

const SEVERITY_TONE: Record<IncidentSeverity, DailyTone> = {
    info: "info",
    minor: "info",
    major: "warning",
    critical: "critical",
};

const LIFECYCLE_TEXT: Record<IncidentLifecycle, string> = {
    pending_confirmation: "Pendiente de confirmar",
    open: "Abierta",
    recovering: "Recuperándose",
    recovered: "Recuperada",
};

const RULE_TEXT: Record<RuleId, string> = {
    exchange_overdue: "Conversión fuera de plazo",
    outflow_overdue: "Pago al destino fuera de plazo",
    total_funded_overdue: "Operación financiada fuera de su plazo total",
    credit_stalled: "Abono de depósito detenido",
    review_backlog: "Revisión manual acumulada",
    projection_lag: "Retraso de la proyección",
    capability_unverifiable: "Capacidad no verificable",
};

export type PlatformStatusVm = {
    stateLabel: string;
    tone: DailyTone;
    scopeLabel: string;
    windowLabel: string;
    timeZone: string;
    qualityLabel: string;
    reasons: string[];
    coverageCritical: boolean;
    coverageLabel: string;
    policyLabel: string;
};

export type CapabilityVm = {
    key: CapabilityKey;
    label: string;
    statusLabel: string;
    tone: DailyTone;
    countsAsHealthy: boolean;
    evidenceModeLabel: string;
    lastEvidenceLabel: string;
    validityLabel: string;
    criticalLabel: string;
    reasons: string[];
};

export type ImpactRowVm = {
    currencyKey: string;
    currency: string | null;
    amount: number;
    currencyLabel: string;
    amountLabel: string;
    sample: number;
};

export type MetricVm = {
    label: string;
    valueLabel: string;
    tone: DailyTone;
    qualityLabel: string;
    sampleLabel: string;
    reasons: string[];
};

export type IncidentVm = {
    incidentId: string;
    episode: number;
    title: string;
    scopeLabel: string;
    severityLabel: string;
    tone: DailyTone;
    lifecycleLabel: string;
    firstObservedLabel: string;
    lastObservedLabel: string;
    ownerLabel: string;
    acknowledgementLabel: string;
    policyLabel: string;
    reasons: string[];
    queueHref: string;
    evidenceHref: string;
    /** SC-011: without the operator role the buttons are not rendered at all. */
    actionsVisible: boolean;
    assignedToViewer: boolean;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const isNumberOrNull = (value: unknown): boolean => value === null || typeof value === "number";

const looksLikeMetric = (value: unknown): boolean =>
    isRecord(value) && isNumberOrNull(value.value) && typeof value.quality === "string";

/**
 * D24: the shared resource cache stores `unknown`, so the caller supplies the type
 * guard that lets a cached payload be read as this DTO without a cast (L1929).
 */
export const isPlatformStatusDto = (value: unknown): value is DailyPlatformStatusDto =>
    isRecord(value)
    && isRecord(value.envelope)
    && typeof value.state === "string"
    && Array.isArray(value.capabilities)
    && typeof value.coverageCritical === "boolean"
    && Array.isArray(value.incidents)
    && looksLikeMetric(value.backlogOpen)
    && looksLikeMetric(value.backlogOverdue)
    && looksLikeMetric(value.throughputCompleted)
    && Array.isArray(value.impactByCurrency)
    && isNumberOrNull(value.oldestOpenAgeMs);

const scopeText = (scope: DailyScopeDto): string => scope.label;

const windowText = (window: DailyWindowDto): string => WINDOW_TEXT[window.label];

const reasonTexts = (codes: ReasonCode[]): string[] => codes.map((code) => REASON_TEXT[code]);

const diagnosticHref = (params: Readonly<Record<string, string>>): string => {
    const search = new URLSearchParams();
    Object.keys(params).sort().forEach((name) => {
        const value = params[name];
        if (value !== "") search.append(name, value);
    });
    const serialized = search.toString();
    return serialized === "" ? "/diagnostic" : `/diagnostic?${serialized}`;
};

export const commandViewModel = {
    /**
     * D36 (ruling de U4): quien APLICA el permiso es quien lo declara. El servidor
     * envía `permissions` en cada payload desde el que se dibujan los botones, así
     * que el FE no vuelve a deducirlo del token. Un payload sin el campo —un servidor
     * anterior a la enmienda, o una respuesta recortada— no es un permiso: la única
     * lectura segura de «no contesta» es «no puede».
     */
    canOperate: (payload: Pick<DailyPlatformStatusDto, "permissions">): boolean =>
        payload.permissions?.canOperate === true,

    windowLabel: windowText,

    qualityLabel: (quality: MetricQuality): string => QUALITY_TEXT[quality],

    reasonTexts,

    /**
     * SC-023: the tone of the header is decided by the evidence first. A screen that
     * paints `insufficient` red tells the operator a failure happened when what
     * actually happened is that nothing could be measured.
     */
    statusTone: (state: HealthState, quality: MetricQuality): DailyTone => {
        if (quality === "insufficient") return QUALITY_TONE.insufficient;
        return HEALTH_TONE[state];
    },

    metricTone: (quality: MetricQuality): DailyTone => QUALITY_TONE[quality],

    status: (dto: DailyPlatformStatusDto): PlatformStatusVm => ({
        stateLabel: HEALTH_TEXT[dto.state],
        tone: commandViewModel.statusTone(dto.state, dto.envelope.quality),
        scopeLabel: scopeText(dto.envelope.scope),
        windowLabel: windowText(dto.envelope.window),
        timeZone: dto.envelope.window.timeZone,
        qualityLabel: QUALITY_TEXT[dto.envelope.quality],
        reasons: reasonTexts(dto.envelope.reasonCodes),
        coverageCritical: dto.coverageCritical,
        coverageLabel: dto.coverageCritical
            ? "Cobertura crítica aprobada y vigente"
            : "Cobertura crítica sin establecer: este estado no equivale a una salud completa",
        policyLabel: dailyFormat.policy(dto.envelope.policy.policyVersion, dto.envelope.policy.policyFingerprint),
    }),

    // B10: a capability the server marks `displayed:false` (e.g. `access`, with no
    // source to prove it) stays in the evaluation but is not drawn.
    capabilities: (dtos: DailyCapabilityDto[]): CapabilityVm[] =>
        dtos.filter((dto) => dto.displayed !== false).map((dto) => ({
            key: dto.key,
            label: CAPABILITY_TEXT[dto.key],
            statusLabel: CAPABILITY_STATUS_TEXT[dto.status],
            tone: CAPABILITY_STATUS_TONE[dto.status],
            countsAsHealthy: CAPABILITY_COUNTS_AS_HEALTHY[dto.status],
            evidenceModeLabel: EVIDENCE_MODE_TEXT[dto.evidenceMode],
            lastEvidenceLabel: dailyFormat.stamp(dto.lastEvidenceAt),
            validityLabel: `${Math.round(dto.validityMs / 60_000)} min`,
            criticalLabel: dto.critical ? "Crítica" : "De apoyo",
            reasons: reasonTexts(dto.reasonCodes),
        })),

    /**
     * FR-008/SC-008: one row per currency. Daily has no FX, so amounts in different
     * currencies are never added together — a combined total would be a fiction the
     * operator cannot act on.
     */
    impactRows: (rows: MoneyByCurrencyDto[]): ImpactRowVm[] => {
        const buckets = new Map<string, { currency: string | null; amount: number; sample: number }>();
        rows.forEach((row) => {
            const key = row.currency ?? "";
            const current = buckets.get(key);
            if (current === undefined) {
                buckets.set(key, { currency: row.currency, amount: row.amount, sample: row.sample });
                return;
            }
            buckets.set(key, {
                currency: current.currency,
                amount: current.amount + row.amount,
                sample: current.sample + row.sample,
            });
        });
        return Array.from(buckets.entries()).map(([currencyKey, bucket]) => ({
            currencyKey,
            currency: bucket.currency,
            amount: bucket.amount,
            currencyLabel: bucket.currency ?? "Moneda no registrada",
            amountLabel: dailyFormat.money(bucket.amount, bucket.currency),
            sample: bucket.sample,
        }));
    },

    metric: (label: string, dto: MetricDto): MetricVm => ({
        label,
        valueLabel: dailyFormat.count(dto.value),
        tone: QUALITY_TONE[dto.quality],
        qualityLabel: QUALITY_TEXT[dto.quality],
        sampleLabel: `${countFormatter.format(dto.sample)} operaciones en la población`,
        reasons: reasonTexts(dto.reasonCodes),
    }),

    incidents: (
        dtos: DailyIncidentDto[],
        options: { canOperate: boolean; viewerId: string },
    ): IncidentVm[] =>
        dtos.map((dto) => ({
            incidentId: dto.incidentId,
            episode: dto.episode,
            title: RULE_TEXT[dto.ruleId],
            scopeLabel: scopeText(dto.scope),
            severityLabel: SEVERITY_TEXT[dto.severity],
            tone: SEVERITY_TONE[dto.severity],
            lifecycleLabel: LIFECYCLE_TEXT[dto.lifecycle],
            firstObservedLabel: dto.firstObservedAtQuality === "evidence_timestamp"
                ? dailyFormat.stamp(dto.firstObservedAt)
                : `${dailyFormat.stamp(dto.firstObservedAt)} (visto primero por el evaluador)`,
            lastObservedLabel: dailyFormat.stamp(dto.lastObservedAt),
            // FR-013: an unowned incident says so. It never borrows the viewer's name.
            ownerLabel: dto.assignee === null
                ? "Sin asignar"
                : dto.assignee === options.viewerId ? "Tú" : dto.assignee,
            acknowledgementLabel: dto.acknowledgedBy === null
                ? "Sin reconocer"
                : `Reconocida ${dailyFormat.stamp(dto.acknowledgedAt)}`,
            policyLabel: dailyFormat.policy(dto.policyVersion, dto.policyFingerprint),
            reasons: reasonTexts(dto.reasonCodes),
            queueHref: dto.links.queue,
            evidenceHref: dto.links.evidence,
            actionsVisible: options.canOperate,
            assignedToViewer: dto.assignee === options.viewerId,
        })),

    diagnosticHref,
};
